import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createHaul, getHaul, updateHaul, HaulFormData, searchProducts } from '@/services/haulService';
import { ProductItem } from '@/services/productsService';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { UploadCloudIcon, X, ChevronDown, Loader2 } from 'lucide-react';

type SelectedProduct = { _id: number; name: string };

const HaulForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [name, setName] = useState('');
  const [youtubeLink, setYoutubeLink] = useState('');
  const [thumbnailImage, setThumbnailImage] = useState<File | string | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [productsOpen, setProductsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Server-side product search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProductItem[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const PER_PAGE = 30;
  const [resolveTrigger, setResolveTrigger] = useState(0);

  // All products cache (for resolving names in edit mode)
  const [allProducts, setAllProducts] = useState<ProductItem[]>([]);

  // Load all products once on mount (used only for name resolution in edit mode)
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await searchProducts('', 1, 2000);
        setAllProducts(data || []);
      } catch (e) {
        console.error('Error loading products:', e);
      }
    };
    load();
  }, []);

  // Server-side search with debounce
  const doSearch = useCallback(async (query: string, page: number, append: boolean) => {
    setSearchLoading(true);
    try {
      const { data, count } = await searchProducts(query, page, PER_PAGE);
      if (append) {
        setSearchResults((prev) => [...prev, ...(data || [])]);
      } else {
        setSearchResults(data || []);
      }
      setSearchTotal(count || 0);
    } catch (e) {
      console.error('Error searching products:', e);
      toast.error('Failed to search products');
      if (!append) setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search when query changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchPage(1);
    debounceRef.current = setTimeout(() => {
      doSearch(searchQuery, 1, false);
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, doSearch]);

  // Reset search when popover opens/closes
  useEffect(() => {
    if (productsOpen) {
      // Load initial results if not yet searched
      if (searchResults.length === 0 && !searchLoading) {
        doSearch('', 1, false);
      }
    } else {
      setSearchQuery('');
      setSearchPage(1);
    }
  }, [productsOpen]);

  const handleLoadMore = () => {
    const nextPage = searchPage + 1;
    setSearchPage(nextPage);
    doSearch(searchQuery, nextPage, true);
  };

  const hasMore = searchResults.length < searchTotal;

  // Load haul in edit mode
  useEffect(() => {
    if (!isEditMode || !id) return;
    const load = async () => {
      try {
        setLoading(true);
        const haul = await getHaul(id);
        setName(haul.name);
        setYoutubeLink((haul as any).youtube_link || '');
        setThumbnailImage(haul.thumbnail_image);
        setThumbnailPreview(haul.thumbnail_image);
        // Resolve names from all products cache
        const list: SelectedProduct[] = (haul.item_ids || []).map((_id) => {
          const p = allProducts.find((pr) => pr._id === _id);
          return { _id, name: p ? (p.name as string) : `Product #${_id}` };
        });
        setSelectedProducts(list);
        setResolveTrigger((t) => t + 1);
      } catch (e) {
        console.error('Error loading haul:', e);
        toast.error('Failed to load haul');
        navigate('/admin/hauls');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEditMode, navigate]);

  // Re-resolve selected product names when allProducts finishes loading (edit mode)
  useEffect(() => {
    if (!isEditMode || allProducts.length === 0) return;
    setSelectedProducts((prev) =>
      prev.map(({ _id, name: n }) => {
        const p = allProducts.find((pr) => pr._id === _id);
        return { _id, name: p ? (p.name as string) : n };
      })
    );
  }, [isEditMode, allProducts, resolveTrigger]);

  // Reset display state when dropdown closes
  useEffect(() => {
    if (!productsOpen) {
      setSearchQuery('');
      setSearchPage(1);
    }
  }, [productsOpen]);

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnailImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setThumbnailPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const removeThumbnail = () => {
    setThumbnailImage(null);
    setThumbnailPreview(null);
  };

  const addProduct = (p: ProductItem) => {
    if (selectedProducts.some((s) => s._id === p._id)) return;
    setSelectedProducts((prev) => [...prev, { _id: p._id, name: p.name as string }]);
  };

  const removeProduct = (_id: number) => {
    setSelectedProducts((prev) => prev.filter((s) => s._id !== _id));
  };

  // Filter out already-selected products from search results
  const displayedProducts = useMemo(() => {
    return searchResults.filter((p) => !selectedProducts.some((s) => s._id === p._id));
  }, [searchResults, selectedProducts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!thumbnailImage) {
      toast.error('Thumbnail image is required');
      return;
    }
    const formData: HaulFormData = {
      name: name.trim(),
      thumbnail_image: thumbnailImage,
      youtube_link: youtubeLink.trim() ? youtubeLink.trim() : null,
      item_ids: selectedProducts.map((s) => s._id),
    };
    try {
      setSaving(true);
      if (isEditMode && id) {
        await updateHaul(id, formData);
        toast.success('Haul updated');
      } else {
        await createHaul(formData);
        toast.success('Haul created');
      }
      navigate('/admin/hauls');
    } catch (err: any) {
      console.error('Error saving haul:', err);
      const errorMessage = err?.message || err?.error?.message || 'Failed to save haul';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10 animate-fade-in">
      <div className="flex items-center">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/hauls')} className="mr-2">
          <X className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{isEditMode ? 'Edit Haul' : 'Add Haul'}</h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update haul details' : 'Create a new haul with a name, thumbnail, and products'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="bg-background border border-border/50 shadow-subtle">
          <CardHeader>
            <CardTitle>Haul details</CardTitle>
            <CardDescription>Name, thumbnail, and products to include</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Haul name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="youtube_link">YouTube link (optional)</Label>
              <Input
                id="youtube_link"
                value={youtubeLink}
                onChange={(e) => setYoutubeLink(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>

            <div className="space-y-2">
              <Label>Thumbnail image</Label>
              <div className="flex items-center justify-center w-full">
                <Label
                  htmlFor="thumb-upload"
                  className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer bg-background/50 hover:bg-background/80 border-border/50 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center py-4">
                    <UploadCloudIcon className="w-8 h-8 mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground">PNG, JPG or GIF</p>
                  </div>
                  <Input
                    id="thumb-upload"
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleThumbnailChange}
                  />
                </Label>
              </div>
              {thumbnailPreview && (
                <div className="relative inline-block mt-2">
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail"
                    className="h-24 w-24 object-cover rounded-md border"
                  />
                  <button
                    type="button"
                    onClick={removeThumbnail}
                    className="absolute -top-1 -right-1 bg-destructive text-white rounded-full p-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Products ({selectedProducts.length} selected)</Label>
              <Popover open={productsOpen} onOpenChange={setProductsOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between">
                    Add products
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[500px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search by product name or ID (e.g. 4694)..."
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList className="max-h-[300px] overflow-y-auto">
                      {searchLoading && displayedProducts.length === 0 ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                        </div>
                      ) : displayedProducts.length === 0 ? (
                        <CommandEmpty>No products found.</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {displayedProducts.map((p) => (
                            <CommandItem key={p._id} onSelect={() => addProduct(p)}>
                              <div className="flex flex-1 items-center justify-between">
                                <span className="truncate">{p.name}</span>
                                <span className="text-muted-foreground text-xs ml-2">#{p._id}</span>
                              </div>
                            </CommandItem>
                          ))}
                          {hasMore && (
                            <CommandItem
                              onSelect={handleLoadMore}
                              className="justify-center text-primary hover:text-primary-foreground hover:bg-primary cursor-pointer"
                            >
                              {searchLoading ? (
                                <span className="flex items-center gap-2">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Loading...
                                </span>
                              ) : (
                                `Load more (${searchTotal - searchResults.length} remaining)`
                              )}
                            </CommandItem>
                          )}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedProducts.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 max-h-[200px] overflow-y-auto p-2 border rounded-md">
                  {selectedProducts.map(({ _id, name: n }) => (
                    <Badge key={_id} variant="secondary" className="flex items-center gap-1 pr-1">
                      <span className="max-w-[120px] truncate">{n}</span>
                      <button
                        type="button"
                        onClick={() => removeProduct(_id)}
                        className="rounded p-0.5 hover:bg-muted"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button type="button" variant="outline" onClick={() => navigate('/admin/hauls')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <span className="h-4 w-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Haul' : 'Create Haul'
              )}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
};

export default HaulForm;